import { createEntityAdapter } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { EntityId, InstructionTypeValueEnum, Instrument, InvestmentOrder, LoadingStatus, OrderStatusEnum } from '@qvs/core';
import * as fromActions from './investment-order.actions';
import { immerOn } from 'ngrx-immer/store';
import { selectAllInstruments, selectInstrumentEntities, selectIsCurrentInstrumentMutualFund } from '~/modules/instrument/slice';
import { onEntityLoadingStatus } from '~/shared/helpers/loading-status-helper';
import { selectAccountFromCurrentPortfolioThatAllowTrading } from '~/modules/accounts/slice';
import { selectCurrentInvestmentOrderRequest } from '~/modules/newInvestmentOrder/slice';
import { isNil } from 'lodash-es';

const investmentOrderEntityAdapter = createEntityAdapter<InvestmentOrder>();

export const initialState = investmentOrderEntityAdapter.getInitialState({
  loadingStatus: LoadingStatus.IDLE,
  details: {
    currentOrderId: undefined as EntityId
  }
});

export type State = typeof initialState;

const reducer = createReducer(
  initialState,
  on(fromActions.setInvestmentOrders, (state, { entities }) => investmentOrderEntityAdapter.setAll(entities, state)),
  on(fromActions.upsertInvestmentOrder, (state, { entity }) => investmentOrderEntityAdapter.upsertOne(entity, state)),
  ...onEntityLoadingStatus<State>(
    fromActions.loadInvestmentOrders,
    fromActions.loadInvestmentOrdersSuccess,
    fromActions.loadInvestmentOrdersFailure
  ),
  ...onEntityLoadingStatus<State>(
    fromActions.loadInvestmentOrder,
    fromActions.loadInvestmentOrderSuccess,
    fromActions.loadInvestmentOrderFailure
  ),
  immerOn<State, [typeof fromActions.setCurrentOrderId]>(fromActions.setCurrentOrderId, (state, { orderId }) => {
    state.details.currentOrderId = orderId;
  }),
  on<State, [typeof fromActions.clearCurrentOrder]>(fromActions.clearCurrentOrder, (state) => {
    const currentOrderId = state.details.currentOrderId;

    let updatedState = state;
    if (!isNil(currentOrderId)) {
      updatedState = investmentOrderEntityAdapter.removeOne(currentOrderId as string, state);
    }

    return { ...updatedState, details: { ...updatedState.details, currentOrderId: undefined } };
  })
);

export const investmentOrderFeatureKey = 'investmentOrders';

export const investmentOrderFeature = createFeature({
  name: investmentOrderFeatureKey,
  reducer
});

const { selectInvestmentOrdersState, selectEntities, selectDetails } = investmentOrderFeature;
export const selectLoadingStatus = createSelector(selectInvestmentOrdersState, (state) => state.loadingStatus);

export const selectAllOrders = createSelector(selectInvestmentOrdersState, investmentOrderEntityAdapter.getSelectors().selectAll);

export const selectAllOrdersForPortfolioWithInstrument = createSelector(
  selectAllOrders,
  selectAllInstruments,
  selectAccountFromCurrentPortfolioThatAllowTrading,
  (investmentOrders, instruments, accounts) =>
    investmentOrders
      .filter((investmentOrder) => accounts.some((account) => account.id === investmentOrder.accountId))
      .map((investmentOrder) => ({
        ...investmentOrder,
        instrument: instruments.find((instrument) => instrument.id === investmentOrder.securityId)
      }))
);

export const selectOrdersForInstrument = (instrument: Instrument) =>
  createSelector(selectAllOrders, (orders) => orders.filter((order) => order.securityId === instrument.id));

export const selectPendingOrdersForInstrument = (instrument: Instrument) =>
  createSelector(selectOrdersForInstrument(instrument), (orders) =>
    orders.filter((order) => order.orderStatus === OrderStatusEnum.PENDING)
  );

export const selectExecutedOrdersForInstrument = (instrument: Instrument) =>
  createSelector(selectOrdersForInstrument(instrument), (orders) =>
    orders.filter((order) => order.orderStatus === OrderStatusEnum.EXECUTED)
  );

export const selectCurrentPortfolioHasOrders = createSelector(
  selectAllOrdersForPortfolioWithInstrument,
  (orders) => orders.length > 0
);

export const selectMostRecentOrdersForPortfolio = (maxResults: number) =>
  createSelector(selectAllOrdersForPortfolioWithInstrument, (orders) => orders.slice(0, maxResults));

export const selectCurrentOrderId = createSelector(selectDetails, (details) => details.currentOrderId);

export const selectCurrentOrder = createSelector(
  selectEntities,
  selectCurrentOrderId,
  (orders, currentOrderId) => orders[currentOrderId] ?? ({} as InvestmentOrder)
);

export const selectOrderById = (orderId: EntityId) =>
  createSelector(selectAllOrders, (orders) => orders.find((o) => o.id === orderId || o.generalOrderId === orderId));

export const selectIsCurrentOrderMarketOrder = createSelector(
  selectCurrentOrder,
  (order) => order.instructionTypeValue === InstructionTypeValueEnum.ORDER_AT_BEST
);

export const selectShowInstructionType = createSelector(
  selectIsCurrentOrderMarketOrder,
  selectIsCurrentInstrumentMutualFund,
  (isMarketOrder, isMutualFund) => !isMarketOrder && !isMutualFund
);

export const selectShowInstructionTypeInPreview = createSelector(
  selectIsCurrentInstrumentMutualFund,
  (isMutualFund) => !isMutualFund
);

export const selectIsCurrentInvestmentOrderMarketOrder = createSelector(
  selectCurrentInvestmentOrderRequest,
  (investmentOrderRequest) => investmentOrderRequest.instructionTypeValue === InstructionTypeValueEnum.ORDER_AT_BEST
);

export const selectOrderInstrument = createSelector(
  selectCurrentOrder,
  selectInstrumentEntities,
  (order, instruments) => instruments[order.securityId] ?? {}
);
